import { Link } from 'react-router-dom';
import Nav from '../components/Nav';
import Footer from '../components/Footer';

const CONTACT_EMAIL = 'support@asmakam.com';
const BUSINESS_ADDRESS = '15 Common St, #488, Natick, MA 01760';
const GOVERNING_JURISDICTION = 'Commonwealth of Massachusetts, USA';

export default function Terms() {
	return (
		<div className='bg-slate-950 text-white min-h-screen'>
			<Nav />
			<main className='mx-auto max-w-4xl px-6 py-20'>
				<h1 className='font-display text-3xl sm:text-5xl font-semibold tracking-tight text-white'>
					Terms & Conditions
				</h1>
				<p className='mt-4 text-base text-blue-200/70'>
					Effective date: 19 September 2025 • Last updated: 19 September 2025
				</p>
				<div className='mt-6 text-base text-blue-100/90'>
					Please also review our{' '}
					<Link
						to='/privacy'
						className='text-blue-400 underline hover:text-blue-300'>
						Privacy Policy
					</Link>
					.
				</div>

				<div className='mt-12 text-lg text-blue-100/80 leading-relaxed space-y-12'>
					<section>
						<h2 className='text-2xl font-semibold text-white mb-6'>
							1. Introduction
						</h2>
						<p className='mb-6'>
							Welcome to the products and services offered by Asmakam LLC
							("Asmakam", "we", "our", or "us"), including the CrikIT platform
							and related SMS messaging programs. These Terms & Conditions
							("Terms") govern your access to and use of our websites,
							platforms, and communications (collectively, the "Services").
						</p>
					</section>

					<section>
						<h2 className='text-2xl font-semibold text-white mb-6'>
							2. Eligibility
						</h2>
						<p className='mb-6'>
							You must be at least the age of majority in your jurisdiction to
							use the Services. By using the Services, you represent and warrant
							that you meet this eligibility requirement.
						</p>
					</section>

					<section>
						<h2 className='text-2xl font-semibold text-white mb-6'>
							3. Account Registration & Security
						</h2>
						<p className='mb-6'>
							Certain features require an account. When creating an account, you
							must provide accurate, current, and complete information and keep
							it updated. You are responsible for safeguarding your credentials
							and for all activities occurring under your account. Notify us
							immediately of any unauthorized use.
						</p>
					</section>

					<section>
						<h2 className='text-2xl font-semibold text-white mb-6'>
							4. Use of the Service
						</h2>
						<p className='mb-6'>
							You agree to use the Services lawfully and in accordance with
							these Terms. You will not engage in abuse, spam, harassment,
							unauthorized scraping, infringement of others' rights, or any
							other behavior prohibited by applicable laws or regulations.
						</p>
					</section>

					<section>
						<h2 className='text-2xl font-semibold text-white mb-6'>
							5. SMS Messaging
						</h2>
						<p className='mb-4'>
							If you opt in, Asmakam may send SMS (text) messages to your mobile
							number for account verification, event notifications, support
							updates, or other service notices.
						</p>
						<ul className='list-disc list-inside space-y-3 text-blue-100/80'>
							<li>Message frequency varies.</li>
							<li>Standard message & data rates may apply.</li>
							<li>
								Reply <span className='font-semibold text-white'>STOP</span> to
								opt out at any time, or{' '}
								<span className='font-semibold text-white'>HELP</span> for
								assistance.
							</li>
							<li>
								Carriers are not responsible for delayed or undelivered
								messages.
							</li>
						</ul>
						<p className='mt-4'>
							These SMS terms form part of these Terms & Conditions.
						</p>
					</section>

					<section>
						<h2 className='text-2xl font-semibold text-white mb-6'>
							6. Privacy Policy
						</h2>
						<p className='mb-6'>
							Your use of the Services is also governed by our{' '}
							<Link
								to='/privacy'
								className='text-blue-400 underline hover:text-blue-300'>
								Privacy Policy
							</Link>
							, which explains how we collect, use, and share personal
							information.
						</p>
					</section>

					<section>
						<h2 className='text-2xl font-semibold text-white mb-6'>
							7. Intellectual Property
						</h2>
						<p className='mb-6'>
							All content, trademarks, logos, designs, and software associated
							with the Services are owned by Asmakam or our licensors and are
							protected by intellectual property laws. You may not reproduce,
							distribute, or create derivative works from our content without
							our express permission.
						</p>
					</section>

					<section>
						<h2 className='text-2xl font-semibold text-white mb-6'>
							8. Disclaimers & Limitation of Liability
						</h2>
						<p className='mb-6'>
							The Services are provided on an "as is" and "as available" basis,
							without warranties of any kind. To the fullest extent permitted by
							law, Asmakam disclaims all implied warranties and will not be
							liable for indirect, incidental, consequential, special, or
							punitive damages arising from or relating to your use of the
							Services.
						</p>
					</section>

					<section>
						<h2 className='text-2xl font-semibold text-white mb-6'>
							9. Indemnification
						</h2>
						<p className='mb-6'>
							You agree to indemnify, defend, and hold harmless Asmakam, its
							officers, directors, employees, and agents from any claims,
							liabilities, losses, damages, or expenses (including reasonable
							attorney fees) arising out of your use of the Services or
							violation of these Terms.
						</p>
					</section>

					<section>
						<h2 className='text-2xl font-semibold text-white mb-6'>
							10. Changes to Terms
						</h2>
						<p className='mb-6'>
							We may revise these Terms periodically. When changes occur, we
							will update the "Last updated" date above. Your continued use of
							the Services after changes become effective constitutes acceptance
							of the revised Terms.
						</p>
					</section>

					<section>
						<h2 className='text-2xl font-semibold text-white mb-6'>
							11. Governing Law & Dispute Resolution
						</h2>
						<p className='mb-6'>
							These Terms are governed by the laws of the{' '}
							{GOVERNING_JURISDICTION}. Any disputes arising under or relating
							to these Terms will be resolved exclusively in the state or
							federal courts located within that jurisdiction, and you consent
							to the personal jurisdiction of those courts.
						</p>
					</section>

					<section>
						<h2 className='text-2xl font-semibold text-white mb-6'>
							12. Contact
						</h2>
						<p className='mb-6'>
							If you have questions about these Terms or our SMS program,
							contact us at{' '}
							<a
								href={`mailto:${CONTACT_EMAIL}`}
								className='text-blue-400 hover:text-blue-300'>
								{CONTACT_EMAIL}
							</a>{' '}
							or write to Asmakam LLC, {BUSINESS_ADDRESS}.
						</p>
						<p>
							<Link
								to='/privacy'
								className='text-blue-400 underline hover:text-blue-300'>
								Read our Privacy Policy
							</Link>
						</p>
					</section>
				</div>
			</main>
			<Footer />
		</div>
	);
}
